import { Injectable } from '@angular/core';
import { ApiService, JwtService } from '../../../../core/services';
import { Observable } from 'rxjs';
import { ITableResults } from '../../../../core/interfaces';
import { HttpUtils } from '../../../../core/utils/HttpUtils';
import { IVoucherSeries } from '../interfaces/IVoucherSeries';
import { IVoucher } from '../interfaces/IVoucher';
import { ICreateVoucherSeries } from '../interfaces/ICreateVoucherSeries';
import { HttpParams } from '@angular/common/http';
import { HttpError } from '../../../../core/decorators/http-error.decorator';
import { VoucherErrorCodes } from '../enums/VoucherErrorCodes';
import { IVoucherHistoryItem } from '../interfaces/IVoucherHistoryItem';
import { IKeyAndValue } from '../../../../core/interfaces/IKeyAndValue';
import { VoucherCodeStatuses } from '../enums/VoucherCodeStatuses';

@Injectable({
  providedIn: 'root'
})
export class VouchersService {

  protected readonly baseUrl = '/vouchers';

  constructor(
    private readonly _apiService: ApiService,
    private readonly _jwtService: JwtService
  ) {}

  @HttpError(VoucherErrorCodes)
  public getVouchersSeries(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filters: Array<{ key: string; value: string[] }>,
    search: Array<{ key: string; value: string[] }>,
    additionalFilters: Array<{ key: string; value: string[] }>,
  ): Observable<ITableResults<IVoucherSeries>> {
    const params = HttpUtils.prepareQueryableParams(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters);

    return this._apiService.get(`${ this.baseUrl }/series`, params );
  }

  @HttpError(VoucherErrorCodes)
  public getVouchersHistory(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filters: Array<{ key: string; value: string[] }>,
    search: Array<{ key: string; value: string[] }>,
    additionalFilters: Array<{ key: string; value: string[] }>,
  ): Observable<ITableResults<IVoucherHistoryItem>> {
    const params = HttpUtils.prepareQueryableParams(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters);

    return this._apiService.get(`${ this.baseUrl }/history-all`, params );
  }

  @HttpError(VoucherErrorCodes)
  public getVouchers(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filters: Array<{ key: string; value: string[] }>,
    search: Array<{ key: string; value: string[] }>,
    additionalFilters: Array<{ key: string; value: string[] }>,
  ): Observable<ITableResults<IVoucher>> {
    const params = HttpUtils.prepareQueryableParams(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters);

    return this._apiService.get(`${ this.baseUrl }`, params );
  }

  @HttpError(VoucherErrorCodes)
  public generateVouchersForSeries(count: number, seriesId: number): Observable<void> {
    return this._apiService.post(`${ this.baseUrl }/async`, { count, seriesId });
  }

  @HttpError(VoucherErrorCodes)
  public createVoucherSeries(series: ICreateVoucherSeries): Observable<void> {
    return this._apiService.post(`${ this.baseUrl }/series`, series);
  }

  @HttpError(VoucherErrorCodes)
  public setVoucherAsRedeemed(voucherCodeId: number): Observable<void> {
    return this._apiService.post(`${ this.baseUrl }/set-as-redeemed`, { id: voucherCodeId });
  }

  @HttpError(VoucherErrorCodes)
  public setVoucherAsSold(voucherCodeId: number): Observable<void> {
    return this._apiService.post(`${ this.baseUrl }/voucher-sold`, { id: voucherCodeId });
  }

  @HttpError(VoucherErrorCodes)
  public getVoucherDetailsByCode(code: string): Observable<IVoucher> {
    const params = new HttpParams().append('code', code);

    return this._apiService.get(`${ this.baseUrl }/code`, params);
  }

  @HttpError(VoucherErrorCodes)
  public getVoucherHistory(id: number): Observable<IVoucherHistoryItem[]> {
    const params = new HttpParams().append('voucherId', id.toString());

    return this._apiService.get(`${ this.baseUrl }/history`, params);
  }

  @HttpError(VoucherErrorCodes)
  public generateCSVFromSeries(seriesId?: number): Observable<Blob> {

    let params = new HttpParams();

    if (seriesId) {
      params = params.append('seriesId', seriesId.toString());
    }

    return this._apiService.getFile(
      `${this._apiService.getApiUrl()}${ this.baseUrl }/generate-csv`,
      'application/csv',
      params
    );
  }

  @HttpError(VoucherErrorCodes)
  public getStatistics(): Observable<IKeyAndValue<VoucherCodeStatuses>[]> {
    return this._apiService.get(`${ this.baseUrl }/statistics`);
  }

  public getDownloadVoucherUrl(voucherId: string): string {
    return `${this._apiService.getApiUrl()}${ this.baseUrl }/download?voucherId=${voucherId}&token=${this._jwtService.getAccessToken()}`;
  }
}
